import { Injectable } from '@angular/core';
import { FeatureFlagTypes } from 'src/app/models/featureFlag/featureFlagsTypes.enum';
import { RouteToFeatureFlagType, RouteToSunsetFlag } from 'src/app/models/featureFlag/RouteToFeatureFlagType.model';
import { RouteToReactMigrationFlag } from 'src/app/models/featureFlag/routeToReactMigrationFlag.model';
import { environment } from 'src/environments/environment';
import { LoggerService } from '../logger/logger.service';
import { UserService } from '../user/user.service';
import { HttpService } from '../../httpServices/http/http.service';
import featureFlagsConfigJson from './feature-flags.json';
import { firstValueFrom } from 'rxjs';
import { catchError } from 'rxjs/operators';
import { of } from 'rxjs';

interface FeatureFlagEnvConfig {
  enabled_orgs: string;
  disabled_orgs?: string;
}

type FeatureFlagConfig = { [envName: string]: FeatureFlagEnvConfig };

type FeatureFlagsConfig = { [featureFlagName: string]: FeatureFlagConfig };

/**
 * Feature flag from API response
 */
interface ApiFeatureFlag {
  key: string;
  value: boolean;
}

/**
 * Feature flags API response
 */
interface FeatureFlagsResponse {
  featureFlags: ApiFeatureFlag[];
}

@Injectable({
  providedIn: 'root',
})
export class FeatureFlagsService {
  private readonly _environmentName: string;
  private readonly _featureFlagsConfig: FeatureFlagsConfig = featureFlagsConfigJson;
  private _apiFeatureFlags: Map<string, boolean> = new Map();
  private _apiFeatureFlagsLoaded = false;

  constructor(
    private readonly _userService: UserService,
    private readonly _logger: LoggerService,
    private readonly _httpService: HttpService
  ) {
    this._environmentName = environment.name;
  }

  /**
   * Initialize feature flags by fetching from the API.
   * The API handles user/account context server-side.
   * This method should be called during app initialization via APP_INITIALIZER.
   * @returns Promise that resolves when flags are loaded
   */
  public async initializeFeatureFlags(): Promise<void> {
    // Check local feature flag config for core_disable_unleash
    if (this._isEnabledInLocalConfig(FeatureFlagTypes.CoreDisableUnleash)) {
      this._logger.info('Feature flags API is disabled via core_disable_unleash flag, using local config only');
      this._apiFeatureFlagsLoaded = false;
      return;
    }

    try {
      const response = await firstValueFrom(
        this._httpService.get<FeatureFlagsResponse>('feature-flags').pipe(
          catchError((error: unknown) => {
            const errorMessage = error instanceof Error ? error.message : String(error);
            this._logger.warn('Failed to load feature flags from API, falling back to local config', {
              error: errorMessage,
            });
            return of({ featureFlags: [] });
          })
        )
      );

      if (response?.featureFlags?.length) {
        this._storeApiFlags(response.featureFlags);
        this._apiFeatureFlagsLoaded = true;
        this._logger.info('Feature flags loaded from API', { count: response.featureFlags.length });
      } else {
        this._logger.warn('Feature flags API returned empty, using local config');
      }
    } catch (error: unknown) {
      const errorMessage = error instanceof Error ? error.message : String(error);
      this._logger.error('Unexpected error initializing feature flags', {
        error: errorMessage,
      });
    }
  }

  /**
   * Store API flags in service.
   * @param flags Array of feature flags from API
   */
  private _storeApiFlags(flags: ApiFeatureFlag[]): void {
    this._apiFeatureFlags.clear();

    for (const flag of flags) {
      this._apiFeatureFlags.set(flag.key, flag.value);
    }
  }

  /**
   * Check if feature is enabled.
   * @param key Feature flag key
   * @returns Whether the feature is enabled
   */
  public isFeatureEnabled(key: string): boolean {
    // Check API flags first if loaded
    if (this._apiFeatureFlagsLoaded) {
      if (this._apiFeatureFlags.has(key)) {
        return this._apiFeatureFlags.get(key) as boolean;
      } else {
        return false;
      }
    }

    // Fall back to local config
    return this._isEnabledInLocalConfig(key);
  }

  public isReactRouteEnabled(routePath: string): boolean {
    const flag = RouteToReactMigrationFlag[routePath];
    if (!flag) return false;
    return this.isFeatureEnabled(flag);
  }

  public canActivateRoute(routeEndpoint: string): boolean {
    if (routeEndpoint in RouteToSunsetFlag) {
      if (this.isFeatureEnabled(RouteToSunsetFlag[routeEndpoint])) {
        return false;
      }
    }

    if (routeEndpoint in RouteToFeatureFlagType) {
      const featureFlagType = RouteToFeatureFlagType[routeEndpoint];
      const enabled = this.isFeatureEnabled(featureFlagType);
      const key = featureFlagType as unknown as string;
      // For flags prefixed with "block_", access should be denied when the flag is enabled
      return key?.startsWith('block_') ? !enabled : enabled;
    }

    return true;
  }

  /**
   * Check if feature is enabled in local config
   */
  private _isEnabledInLocalConfig(key: string): boolean {
    const featureFlagConfig: FeatureFlagConfig = this._featureFlagsConfig[key];
    if (!featureFlagConfig) {
      this._logger.error("Feature flag doesn't exist: " + key);
      return false;
    }

    const featureFlagEnvConfig: FeatureFlagEnvConfig = featureFlagConfig[this._environmentName];
    if (!featureFlagEnvConfig) {
      return false;
    }

    if (featureFlagEnvConfig.disabled_orgs === '*') {
      return false;
    }

    if (featureFlagEnvConfig.disabled_orgs && this._isOrgIncludedInOrgsStr(featureFlagEnvConfig.disabled_orgs)) {
      return false;
    }

    if (featureFlagEnvConfig.enabled_orgs === '*') {
      return true;
    }

    return this._isOrgIncludedInOrgsStr(featureFlagEnvConfig.enabled_orgs);
  }

  private _isOrgIncludedInOrgsStr(orgs_str: string) {
    return orgs_str
      .split(',')
      .map((org) => org.trim())
      .includes(this._userService.zenityAccountId);
  }
}
